import { useCallback, useMemo, useRef, useEffect, lazy } from 'react';
import { cn } from '@divlab/divanui';
import { useInView } from 'react-intersection-observer';

import useNavigation from '@Navigation/useNavigation';
import { useUpdateComponentStorage } from '@Stores/ComponentStorage';
import * as Configurator from '@Stores/Configurator';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import * as Filtrator from '@Stores/Filtrator';
import * as ConfiguratorQuery from '@Queries/Configurator';
import * as Meta from '@Queries/Meta';
import useOperatorStatus from '@Queries/useOperatorStatus';
import useTranslation from '@Queries/useTranslation';
import useAIProductConstructorControl from '@Queries/useAIProductConstructorControl';
import useMedias from '@Hooks/useMedias';
import useSearchParams from '@Hooks/useSearchParams';
import MattressesLayers from '@Pages/PageProduct/elements/MattressesLayers';
import Suspense from '@Components/Suspense';
import ErrorBoundary from '@Components/ErrorBoundary';
import NanoProductCard from '@Components/NanoProductCard';
import InstagramSection from '@Components/InstagramSection';
import ProductsSection from '@Components/ProductsSection';
import useScrollToRef from './hooks/useScrollToRef';
import ComfortBuy from './elements/ComfortBuy';
import StickyBuyButton from './elements/StickyBuyButton';
import AdditionalInfo from './elements/AdditionalInfo';
import QualityLaboratoryBanner from './elements/QualityLaboratoryBanner';
import SaleSoonButton from './elements/SaleSoonButton';
import Main from './elements/sections/Main';
import Delivery from './elements/sections/Delivery';
import Reviews from './elements/sections/Reviews';
import styles from './PageProduct.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';

const PanelOperator = lazy(() => import('@Components/Operator/PanelOperator'));
const ActionBar = lazy(() => import('./elements/ActionBar'));

const PageProduct: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, ...restProps } = props;
  const location = useNavigation();
  const page = PageProductStore.useData();
  const aiControl = useAIProductConstructorControl();
  const { isMobileM } = useMedias();
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const meta = Meta.useMeta();
  const { data: operator } = useOperatorStatus();
  const refCharacteristics = useRef<HTMLDivElement>();
  const refReviews = useRef<HTMLDivElement>();
  const refTop = useRef<HTMLDivElement>();
  const refComfortBuy = useRef<HTMLDivElement>();
  const refActionBar = useRef<HTMLDivElement>();
  const refRelatedProducts = useRef<HTMLDivElement>();
  const modules = PageProductStore.useModules();
  const { handleScrollTo } = useScrollToRef();
  const qualityLaboratory = page.qualityLaboratory;
  const activeMobilePanel = Configurator.useActiveMobilePanel();
  const idViewerModel = 'pageProduct';
  const priceblock = useRef<HTMLDivElement>(null);
  const isComingSoon = page.product.status === 'ComingSoon';
  const { data: configurator, isFetching } = ConfiguratorQuery.useConfigurator();
  const changeableParameters = PageProductStore.useChangeableParameters();
  const entityId = 'configurator-textile';
  const aiViewPage = aiControl.view;
  const schemeImage = page.schemes?.length > 0 ? page.schemes[0].image : null;
  const isCrossSalesProducts = page.crossSalesProducts.products?.length > 0;
  const isQualityLaboratoryBanner = isMobileM && qualityLaboratory?.list?.length > 0;
  const isHouseFurnishing = page.instagram?.length > 0;
  const isOtherVariants = page.sameProducts.products?.length > 0;
  const isUpSales = page.upSalesProducts.products?.length > 0;
  const isHistoryProducts = page.historyProducts.products?.length > 0;

  const goodsInfo = useMemo<PecomDeliveryParameters[]>(() => {
    const productParameters = [];
    if (page.isModular) {
      modules.forEach((module) => {
        if (module.count > 0) {
          productParameters.push({
            id: module.id,
            quantity: module.count,
          });
        }
      });
    } else {
      productParameters.push({
        id: page.product.id,
        quantity: 1,
      });
    }
    return productParameters;
  }, [page.isModular, modules, page.product.id]);

  const tabWithFilter = useMemo(() => {
    return configurator.tabs.find((tab) => !!tab.filtersInfo);
  }, [configurator.tabs]);

  const filters = useMemo(() => {
    if (tabWithFilter) return tabWithFilter.filtersInfo;

    return { filters: [], parameterValues: [], parameters: {} };
  }, [tabWithFilter]);

  Filtrator.init(entityId, {
    ...filters,
    totalCount: tabWithFilter?.count,
    countOfPriorityFilters: 0,
    withoutUrl: true,
  });

  const hasConfigurator = useMemo(() => {
    const selectParameters =
      page.parameters.filter((parameter) => parameter.view === 'select') || [];

    return page.isConfigurator || changeableParameters?.length > 0 || selectParameters?.length > 0;
  }, [changeableParameters?.length, page.isConfigurator, page.parameters]);

  const handleClickCharacteristics = useCallback(() => {
    handleScrollTo(refCharacteristics, refActionBar.current?.clientHeight);
  }, [handleScrollTo]);

  const handleClickReviews = useCallback(() => {
    handleScrollTo(refReviews, refActionBar.current?.clientHeight);
  }, [handleScrollTo]);

  const handleClickTop = useCallback(() => {
    priceblock.current = document.getElementById('priceBlock') as HTMLDivElement;

    handleScrollTo(priceblock);
  }, [handleScrollTo]);

  const handleClickDelivery = useCallback(() => {
    handleScrollTo(refComfortBuy, refActionBar.current?.clientHeight);
  }, [handleScrollTo]);

  /** Opens send review modal when page is loaded and there is a url parameter */
  useEffect(() => {
    const hasReviewUrlParameter = searchParams.has('review');

    if (!hasReviewUrlParameter) return;

    const orderId = searchParams.get('review');

    ModalWindows.open('SendReview', { product: page.product, orderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    PageProductStore.initViewerModelStore(idViewerModel);

    return () => PageProductStore.clear(idViewerModel);
  }, []);

  const { ref: trackingSidebarRef, inView } = useInView({
    rootMargin: '-100px',
    threshold: 0,
  });

  const viewActionBar = useMemo(() => {
    if (isMobileM) return !activeMobilePanel;

    return inView;
  }, [inView, isMobileM, activeMobilePanel]);

  useEffect(() => {
    Configurator.set(configurator.configuration.selectedOptions);
  }, [configurator.configuration.selectedOptions]);

  // показываем панель только после того как новые данные конфигуратора подруженны и произошла смена url
  useEffect(() => {
    if (isFetching) return;

    Configurator.changePanelView(true);

    const timer = setTimeout(() => {
      Configurator.changePanelView(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [location?.pathname, location?.search, isFetching]);

  useUpdateComponentStorage({
    ...(page.isAvailable &&
      !isComingSoon && {
        buyButton: <StickyBuyButton visible={true} configurator={configurator} />,
      }),
    ...(!page.isAvailable &&
      isComingSoon && {
        saleSoon: <SaleSoonButton visible={true} product={page.product} />,
      }),
  });

  return (
    <div
      {...restProps}
      ref={refTop}
      className={cn(
        styles.page,
        {
          [styles.viewDefault]: aiViewPage === 'default',
          [styles.view1]: aiViewPage === 'view-1',
          [styles.view2]: aiViewPage === 'view-2',
        },
        className,
      )}
      data-testid='page-product'
    >
      <ErrorBoundary>
        <Suspense fallback={null}>
          <ActionBar
            ref={refActionBar}
            inView={viewActionBar}
            image={page.gallery[0]?.src}
            product={page.product}
            isConfigurator={page.isConfigurator}
            reviews={page.reviewsSubgallery}
            configurator={configurator}
            handleClickTop={handleClickTop}
            handleClickReviews={handleClickReviews}
            handleClickCharacteristics={handleClickCharacteristics}
            handleClickDelivery={handleClickDelivery}
            hasConfigurator={hasConfigurator}
          />
        </Suspense>
      </ErrorBoundary>
      <Main
        page={page}
        goodsInfo={goodsInfo}
        trackingSidebarRef={trackingSidebarRef}
        onClickReviews={handleClickReviews}
        onClickCharacteristics={handleClickCharacteristics}
        className={styles.main}
      />

      <AdditionalInfo
        configurator={configurator}
        ref={refCharacteristics}
        data-onboardid='characteristics'
        data-testid='characteristics'
        className={styles.characteristics}
      />

      {schemeImage && (
        <MattressesLayers
          schemeImage={schemeImage}
          layers={page.layers}
          priorityParameter={page.priorityParameter}
          className={styles.layers}
        />
      )}

      {isCrossSalesProducts && (
        <section ref={refRelatedProducts} className={styles.offer}>
          <ProductsSection
            title={page.crossSalesProducts.title}
            tabs
            products={page.crossSalesProducts.products}
            analyticsTitle='Вам подойдет'
            data-testid='cross-sales-products'
          />
        </section>
      )}

      {isQualityLaboratoryBanner && (
        <QualityLaboratoryBanner
          className={styles.qualityLaboratory}
          qualityLaboratoryData={qualityLaboratory}
        />
      )}

      <Reviews
        className={styles.feedback}
        ref={refReviews}
        product={page.product}
        refActionBar={refActionBar}
        reviewsSubgallery={page.reviewsSubgallery}
      />

      {isHouseFurnishing && (
        <InstagramSection
          className={styles.houseFurnishing}
          title={t('titles.instagram')}
          posts={page.instagram}
          footnote={page.seoPreFooter}
          data-testid='house-furnishing'
        />
      )}

      {page.deliveryPage && (
        <Delivery
          className={styles.delivery}
          title={page.deliveryPage.title}
          delivery={page.deliveryPage}
          goodsInfo={goodsInfo}
        />
      )}

      <ComfortBuy className={styles.comfortBuy} ref={refComfortBuy} />

      {isOtherVariants && (
        <ProductsSection
          className={styles.other}
          title={page.sameProducts.title}
          products={page.sameProducts.products}
          data-testid='other-variants'
        />
      )}

      {isUpSales && (
        <ProductsSection
          className={styles.upSales}
          title={page.upSalesProducts.title}
          tabs
          products={page.upSalesProducts.products}
          data-testid='similar-item'
        />
      )}

      {isHistoryProducts && (
        <ProductsSection
          className={styles.sectionHistory}
          cnSlide={styles.miniProduct}
          title={t('titles.recently-seen')}
          products={page.historyProducts.products}
          renderProduct={(product) => <NanoProductCard product={product} />}
          analyticsTitle='Вы недавно смотрели'
          data-testid='similar-item'
        />
      )}
      {
        // TODO: meta.data.isOperatorTest - флаг для стресс-теста календаря доставок от 1С. Удалить по окончанию
        (operator?.isOperatorTest ||
          (!isMobileM && operator?.isOperator && operator?.isOperatorPopupShow)) && (
          <ErrorBoundary>
            <Suspense fallback={null}>
              <PanelOperator
                className={cn(styles.operator, {
                  [styles.test]: !!operator?.isOperatorTest,
                })}
                productId={configurator.configuration.id}
                region={meta.data.region}
              />
            </Suspense>
          </ErrorBoundary>
        )
      }
    </div>
  );
};

export default PageProduct;
